/**
 * LandingNavbar Component
 * 
 * OPARA v7.5 - Locale-Aware Navigation with Language Dropdown
 * All links stay within the current language folder
 */

import React, { useState } from 'react';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Globe, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from '../hooks/useTranslation';

// Language options with flags
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', path: '/en/' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', path: '/tr/' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', path: '/de/' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', path: '/fr/' },
  { code: 'es', name: 'Español', flag: '🇪🇸', path: '/es/' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', path: '/ar/' },
];

const LandingNavbar = ({ onOpenWelcome }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, locale, isRTL } = useTranslation();

  // Get locale-aware paths - ALL routes must stay within locale folder
  const getLocalePath = (route) => {
    const routes = {
      en: {
        home: '/en/',
        pricing: '/en/pricing',
        login: '/en/login',
        settings: '/en/settings'
      },
      tr: {
        home: '/tr/',
        pricing: '/tr/kredi-paketleri',
        login: '/tr/giris',
        settings: '/tr/hesap-ayarlari'
      },
      ar: {
        home: '/ar/',
        pricing: '/ar/pricing',
        login: '/ar/login',
        settings: '/ar/settings'
      },
      fr: {
        home: '/fr/',
        pricing: '/fr/pricing',
        login: '/fr/login',
        settings: '/fr/settings'
      },
      de: {
        home: '/de/',
        pricing: '/de/pricing',
        login: '/de/login',
        settings: '/de/settings'
      },
      es: {
        home: '/es/',
        pricing: '/es/pricing',
        login: '/es/login',
        settings: '/es/settings'
      }
    };
    
    return routes[locale]?.[route] || routes.en[route];
  };

  // Handle language change
  const handleLanguageChange = (langPath) => {
    navigate(langPath);
  };

  // Get plan display name based on locale
  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': t('plan.free'),
      'basic': 'Basic',
      'pro': 'Pro',
      'business': 'Business',
      'enterprise': 'Enterprise',
      'persona_staff': t('plan.staff'),
      'admin': 'Admin',
      'pro_monthly': 'Pro',
      'business_monthly': 'Business'
    };
    return planNames[planType] || planType || t('plan.free');
  };

  // Get current language info
  const currentLang = LANGUAGES.find(l => l.code === locale) || LANGUAGES[0];
  
  return (
    <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-2 sm:px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="mb-1 px-2 sm:px-4">
        <div className="flex justify-between items-center gap-2 sm:gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
          {/* Left: Logo - Goes to current locale's home */}
          <div 
            className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
            onClick={() => navigate(getLocalePath('home'))}
            data-testid="nav-logo"
          >
            <img 
              src="/opara-logo-trimmed.png" 
              alt="OPARA Logo" 
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </div>

          {/* Right: Conditional rendering based on user login status */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {user ? (
              <>
                {/* Credits Box - OPARA v11.5: Font size increased by 1 level */}
                <div className="text-base bg-gray-800 px-3 sm:px-4 rounded-lg border border-gray-700 h-9 sm:h-10 flex items-center">
                  <span className="text-white hidden sm:inline">{t('nav.credits')} </span>
                  <span className="text-white font-bold text-base sm:text-lg">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                {/* Upgrade Button - OPARA v11.5: Font size increased by 1 level */}
                <Link to={getLocalePath('pricing')}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base h-9 sm:h-10 px-3 sm:px-5">
                    {t('nav.upgrade')}
                  </Button>
                </Link>
                
                {/* Avatar Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                          {user?.plan_type === "persona_staff" && (
                            <span className="text-xs text-[#FFDD59] ml-2">{t('nav.staff_badge')}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem 
                      className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800"
                      onClick={() => navigate(getLocalePath('settings'))}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav.account_settings')}
                    </DropdownMenuItem>
                    
                    {/* Language Submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer text-gray-300 hover:text-black focus:text-black data-[state=open]:text-black hover:bg-gray-100 focus:bg-gray-100 data-[state=open]:bg-gray-100">
                        <Globe className="w-4 h-4 mr-2" />
                        {t('nav.change_language')}
                        <span className="ml-auto text-lg">{currentLang.flag}</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="bg-gray-900 border-gray-700">
                          {LANGUAGES.map((lang) => (
                            <DropdownMenuItem
                              key={lang.code}
                              className={`cursor-pointer hover:bg-gray-800 ${
                                locale === lang.code 
                                  ? 'text-[#FFDD59] bg-gray-800' 
                                  : 'text-gray-300 hover:text-white'
                              }`}
                              onClick={() => handleLanguageChange(lang.path)}
                            >
                              <span className="text-lg mr-2">{lang.flag}</span>
                              {lang.name}
                              {locale === lang.code && <span className="ml-auto">✓</span>}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem 
                      onClick={logout}
                      className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Language Dropdown for guests */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="text-gray-300 hover:text-white hover:bg-gray-800 px-1.5 sm:px-2 h-8 sm:h-9"
                    >
                      <span className="text-base sm:text-lg mr-0.5 sm:mr-1">{currentLang.flag}</span>
                      <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-gray-400 text-xs">
                      {t('nav.change_language')}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    {LANGUAGES.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        className={`cursor-pointer hover:bg-gray-800 ${
                          locale === lang.code 
                            ? 'text-[#FFDD59] bg-gray-800' 
                            : 'text-gray-300 hover:text-white'
                        }`}
                        onClick={() => handleLanguageChange(lang.path)}
                      >
                        <span className="text-lg mr-2">{lang.flag}</span>
                        {lang.name}
                        {locale === lang.code && <span className="ml-auto">✓</span>}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Pricing & Login buttons - responsive */}
                <Link to={getLocalePath('pricing')}>
                  <Button 
                    className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold px-2 sm:px-4 py-1 text-xs sm:text-sm h-8 sm:h-9 whitespace-nowrap"
                    data-testid="nav-pricing-btn"
                  >
                    {t('nav.pricing')}
                  </Button>
                </Link>
                
                <Link to={getLocalePath('login')}>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-2 sm:px-4 py-1 text-xs sm:text-sm h-8 sm:h-9 whitespace-nowrap"
                    data-testid="nav-signin-btn"
                  >
                    {t('nav.signin')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default LandingNavbar;
